/* eslint-disable eag/match-named-export -- Pre-existing grab-bag file with several exports */
import {z} from '@hono/zod-openapi';

import {automarketplEquipmentCatalogueRequestSchema} from './automarketplEquipmentCatalogueRequestSchema';

export const equipmentMappingCoverageResultSchema = z
  .enum([
    'mapped',
    'taxonomy_missing',
    'ambiguous',
    'rule_invalid',
    'explicitly_unsupported',
    'evaluation_error',
  ])
  .openapi('EquipmentMappingCoverageResult');

export const equipmentMappingCoverageTriggerSourceSchema = z
  .enum([
    'manual',
    'catalogue_change',
    'taxonomy_change',
    'staged_validation',
    'automation',
    'ai_mapping',
  ])
  .openapi('EquipmentMappingCoverageTriggerSource');

export const equipmentMappingTargetSchema = z
  .object({
    taxonomyType: z.string(),
    externalId: z.string(),
    label: z.string().nullable(),
    semanticRelation: z
      .enum(['equivalent', 'broader', 'narrower', 'different', 'insufficient_evidence'])
      .optional(),
    equivalenceProbability: z.number().min(0).max(1).optional(),
    selectionProbability: z.number().finite().min(0).max(1).optional(),
    selectionConfidence: z.number().finite().min(0).max(1).optional(),
    selectionNoneProbability: z.number().finite().min(0).max(1).optional(),
    selectedByModel: z.boolean().optional(),
    model: z.string().optional(),
  })
  .openapi('EquipmentMappingTarget');

export const equipmentCoverageMetricSchema = z
  .object({
    total: z.number().int().nonnegative(),
    mapped: z.number().int().nonnegative(),
    taxonomyMissing: z.number().int().nonnegative(),
    ambiguous: z.number().int().nonnegative(),
    ruleInvalid: z.number().int().nonnegative(),
    explicitlyUnsupported: z.number().int().nonnegative(),
    evaluationError: z.number().int().nonnegative(),
    mappingCoverage: z.number().min(0).max(1),
    resolvedCoverage: z.number().min(0).max(1),
  })
  .openapi('EquipmentCoverageMetric');

export const equipmentCoverageSummarySchema = equipmentCoverageMetricSchema
  .extend({
    byTaxonomyType: z.record(equipmentCoverageMetricSchema),
  })
  .openapi('EquipmentCoverageSummary');

export const equipmentMappingActivationBlockerSchema = z
  .enum([
    'run_not_completed',
    'configuration_not_staged',
    'ai_mapping_requires_validation',
    'result_unavailable',
    'rule_invalid',
    'new_ambiguity',
    'evaluation_error',
  ])
  .openapi('EquipmentMappingActivationBlocker');

export const equipmentMappingCoverageRunSchema = z
  .object({
    id: z.string().uuid(),
    platformCode: z.string(),
    equipmentCatalogueSnapshotId: z.string().uuid(),
    platformTaxonomySnapshotId: z.string().uuid(),
    mappingRuleSetId: z.string().uuid(),
    mappingRuleSetRevision: z.number().int().positive(),
    configurationBundleId: z.string().uuid(),
    configurationStatus: z.enum(['staged', 'active', 'superseded', 'rejected']),
    vehicleContext: automarketplEquipmentCatalogueRequestSchema.nullish(),
    triggeredBy: z.string(),
    triggerSource: equipmentMappingCoverageTriggerSourceSchema,
    canActivate: z.boolean(),
    activationBlockers: z.array(equipmentMappingActivationBlockerSchema),
    activationBlockerCount: z.number().int().nonnegative(),
    status: z.enum(['queued', 'running', 'completed', 'failed']),
    totalCount: z.number().int().nonnegative(),
    processedCount: z.number().int().nonnegative(),
    resultSummary: equipmentCoverageSummarySchema.nullable(),
    failureReason: z.string().nullable(),
    detailsExpireAt: z.string().datetime(),
    startedAt: z.string().datetime().nullable(),
    completedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .openapi('EquipmentMappingCoverageRun');

export const equipmentMappingCoverageIssueSchema = z
  .object({
    id: z.string().uuid(),
    coverageRunId: z.string().uuid(),
    sourceKey: z.string(),
    sourceLabel: z.string().nullable(),
    result: equipmentMappingCoverageResultSchema,
    selectedTarget: equipmentMappingTargetSchema.nullable(),
    candidates: z.array(equipmentMappingTargetSchema),
    appliedRuleRevisionId: z.string().uuid().nullable(),
    remediationReason: z.string().nullable().optional(),
    remediationStatus: z.enum(['pending', 'validation_queued']).nullable().optional(),
    createdAt: z.string().datetime(),
  })
  .openapi('EquipmentMappingCoverageIssue');

export const equipmentMappingCoverageIssueDetailSchema = equipmentMappingCoverageIssueSchema
  .extend({
    configurationBundleId: z.string().uuid(),
    platformTaxonomySnapshotId: z.string().uuid(),
    mappingRuleSetId: z.string().uuid(),
  })
  .openapi('EquipmentMappingCoverageIssueDetail');

export const equipmentMappingCoverageListQuerySchema = z
  .object({
    limit: z.coerce.number().int().min(1).max(250).default(50),
    offset: z.coerce.number().int().nonnegative().default(0),
  })
  .openapi('EquipmentMappingCoverageListQuery');

export const equipmentMappingCoverageIssuesQuerySchema = equipmentMappingCoverageListQuerySchema
  .extend({
    scope: z.enum(['all', 'actionable']).optional(),
    result: equipmentMappingCoverageResultSchema.optional(),
    search: z.string().trim().max(100).optional(),
  })
  .openapi('EquipmentMappingCoverageIssuesQuery');

export const equipmentMappingCoverageIssueCountsSchema = z
  .object({
    all: z.number().int().nonnegative(),
    actionable: z.number().int().nonnegative(),
    mapped: z.number().int().nonnegative(),
    taxonomyMissing: z.number().int().nonnegative(),
    ambiguous: z.number().int().nonnegative(),
    ruleInvalid: z.number().int().nonnegative(),
    explicitlyUnsupported: z.number().int().nonnegative(),
    evaluationError: z.number().int().nonnegative(),
  })
  .openapi('EquipmentMappingCoverageIssueCounts');

export const equipmentMappingCoverageIssuesResponseSchema = z
  .object({
    items: z.array(equipmentMappingCoverageIssueSchema),
    pagination: z.object({
      limit: z.number().int().positive(),
      offset: z.number().int().nonnegative(),
      total: z.number().int().nonnegative(),
    }),
    counts: equipmentMappingCoverageIssueCountsSchema,
  })
  .openapi('EquipmentMappingCoverageIssuesResponse');

export const enqueueEquipmentMappingCoverageResponseSchema = z
  .object({
    runId: z.string().uuid(),
  })
  .openapi('EnqueueEquipmentMappingCoverageResponse');

export const equipmentMappingRemediationPreviewSchema = z
  .object({
    affectedCaseCount: z.number().int().nonnegative(),
    newlyMappedCount: z.number().int().nonnegative(),
    newlyMissingCount: z.number().int().nonnegative(),
    changedTargetCount: z.number().int().nonnegative(),
    newAmbiguityCount: z.number().int().nonnegative(),
    examples: z.array(
      z.object({
        sourceKey: z.string(),
        sourceLabel: z.string().nullable(),
        before: z.object({
          result: equipmentMappingCoverageResultSchema,
          selectedTarget: equipmentMappingTargetSchema.nullable(),
        }),
        after: z.object({
          result: equipmentMappingCoverageResultSchema,
          selectedTarget: equipmentMappingTargetSchema.nullable(),
        }),
      })
    ),
    nextRuleSetRevision: z.number().int().positive(),
  })
  .openapi('EquipmentMappingRemediationPreview');

export const equipmentMappingRemediationResponseSchema = z
  .object({
    remediationId: z.string().uuid(),
    pendingCount: z.number().int().positive(),
  })
  .openapi('EquipmentMappingRemediationResponse');

export const removeEquipmentMappingRemediationResponseSchema = z
  .object({
    remediationId: z.string().uuid(),
    pendingCount: z.number().int().nonnegative(),
  })
  .openapi('RemoveEquipmentMappingRemediationResponse');

export const equipmentMappingRemediationValidationResponseSchema = z
  .object({
    remediationCount: z.number().int().positive(),
    mappingRuleSetId: z.string().uuid(),
    configurationBundleId: z.string().uuid(),
    coverageRunId: z.string().uuid(),
  })
  .openapi('EquipmentMappingRemediationValidationResponse');

export const equipmentMappingCoverageActivationResponseSchema = z
  .object({
    platformCode: z.string(),
    configurationBundleId: z.string().uuid(),
    coverageRunId: z.string().uuid(),
    activatedAt: z.string().datetime(),
  })
  .openapi('EquipmentMappingCoverageActivationResponse');

export const equipmentMappingCoverageComparisonKindSchema = z
  .enum(['rerun', 'catalogue_change', 'taxonomy_change', 'rule_change', 'mixed_change'])
  .openapi('EquipmentMappingCoverageComparisonKind');

export const equipmentMappingCoverageMetricDeltaSchema = z
  .object({
    total: z.number().int(),
    mapped: z.number().int(),
    taxonomyMissing: z.number().int(),
    ambiguous: z.number().int(),
    ruleInvalid: z.number().int(),
    explicitlyUnsupported: z.number().int(),
    evaluationError: z.number().int(),
    mappingCoverage: z.number(),
    resolvedCoverage: z.number(),
  })
  .openapi('EquipmentMappingCoverageMetricDelta');

export const equipmentMappingCoverageTransitionCountsSchema = z
  .object({
    newlyMapped: z.number().int().nonnegative(),
    newlyMissing: z.number().int().nonnegative(),
    newAmbiguity: z.number().int().nonnegative(),
    resolvedAmbiguity: z.number().int().nonnegative(),
    targetChanged: z.number().int().nonnegative(),
    unchanged: z.number().int().nonnegative(),
  })
  .openapi('EquipmentMappingCoverageTransitionCounts');

export const equipmentMappingCoverageComparisonQuerySchema = z
  .object({
    baselineRunId: z.string().uuid().optional(),
  })
  .openapi('EquipmentMappingCoverageComparisonQuery');

export const equipmentMappingCoverageComparisonResponseSchema = z
  .object({
    currentRunId: z.string().uuid(),
    baselineRunId: z.string().uuid(),
    kind: equipmentMappingCoverageComparisonKindSchema,
    summaryDelta: equipmentMappingCoverageMetricDeltaSchema,
    byTaxonomyType: z.record(equipmentMappingCoverageMetricDeltaSchema),
    transitions: equipmentMappingCoverageTransitionCountsSchema.nullable(),
    examples: z.array(
      z.object({
        sourceKey: z.string(),
        sourceLabel: z.string().nullable(),
        transition: z.enum([
          'newly_mapped',
          'newly_missing',
          'new_ambiguity',
          'resolved_ambiguity',
          'target_changed',
          'unchanged',
        ]),
        before: z.object({
          result: equipmentMappingCoverageResultSchema,
          selectedTarget: equipmentMappingTargetSchema.nullable(),
        }),
        after: z.object({
          result: equipmentMappingCoverageResultSchema,
          selectedTarget: equipmentMappingTargetSchema.nullable(),
        }),
      })
    ),
    detailsAvailable: z.boolean(),
  })
  .openapi('EquipmentMappingCoverageComparisonResponse');

export const equipmentMappingActivationEventKindSchema = z
  .enum(['activation', 'rollback'])
  .openapi('EquipmentMappingActivationEventKind');

export const equipmentMappingActivationEventSchema = z
  .object({
    id: z.string().uuid(),
    platformCode: z.string(),
    fromBundleId: z.string().uuid().nullable(),
    toBundleId: z.string().uuid(),
    coverageRunId: z.string().uuid(),
    kind: equipmentMappingActivationEventKindSchema,
    reason: z.string().nullable(),
    createdBy: z.string(),
    createdAt: z.string().datetime(),
    fromRuleSetRevision: z.number().int().positive().nullable(),
    toRuleSetRevision: z.number().int().positive(),
  })
  .openapi('EquipmentMappingActivationEvent');

export const equipmentMappingRollbackRequestSchema = z
  .object({
    reason: z.string().trim().min(1).max(1000),
    expectedCurrentBundleId: z.string().uuid(),
  })
  .openapi('EquipmentMappingRollbackRequest');
